import type { WorkloadMetricsEntry, WorkloadMetricsInterval } from '@/lib/urm-api';
import {
  GROUPING_DIMENSIONS,
  TOP_BUCKET_LIMIT,
  type GroupingDimension,
  type WorkloadGroupSummary,
  type WorkloadHealthPoint,
  type WorkloadHeatmap,
  type WorkloadRequestTimeSummary,
  type WorkloadSeries,
  type WorkloadShareSeries,
} from './types';

const NS_PER_MS = 1_000_000;

export function listWorkloadClients(intervals: WorkloadMetricsInterval[]): string[] {
  return Array.from(new Set(
    intervals.flatMap((interval) => interval.entries.map((entry) => entry.username)),
  )).sort((a, b) => a.localeCompare(b));
}

export function filterWorkloadIntervalsByClient(
  intervals: WorkloadMetricsInterval[],
  client: string | null,
): WorkloadMetricsInterval[] {
  return filterWorkloadIntervalsByClients(intervals, client === null ? null : [client]);
}

export function filterWorkloadIntervalsByClients(
  intervals: WorkloadMetricsInterval[],
  clients: string[] | null,
): WorkloadMetricsInterval[] {
  if (clients === null) return intervals;
  const selected = new Set(clients);
  return intervals.map((interval) => ({
    ...interval,
    entries: interval.entries.filter((entry) => selected.has(entry.username)),
  }));
}

function dimensionValue(entry: WorkloadMetricsEntry, dimension: GroupingDimension): string {
  if (dimension === 'username') return entry.username;
  if (dimension === 'resourceType') return entry.resource_type;
  if (dimension === 'action') return entry.action;
  return entry.api_label;
}

function bucketKey(entry: WorkloadMetricsEntry, dimensions: GroupingDimension[]): string {
  return JSON.stringify(dimensions.map((dimension) => dimensionValue(entry, dimension)));
}

function bucketLabel(entry: WorkloadMetricsEntry, dimensions: GroupingDimension[]): string {
  if (dimensions.length === 0) return 'All workload';
  return dimensions.map((dimension) => dimensionValue(entry, dimension) || 'Unknown').join(' · ');
}

function aggregateTotals(
  intervals: WorkloadMetricsInterval[],
  dimensions: GroupingDimension[],
): Map<string, { entry: WorkloadMetricsEntry; processingNs: number; requests: number }> {
  const totals = new Map<string, { entry: WorkloadMetricsEntry; processingNs: number; requests: number }>();
  for (const interval of intervals) {
    for (const entry of interval.entries) {
      const key = bucketKey(entry, dimensions);
      const total = totals.get(key) ?? { entry, processingNs: 0, requests: 0 };
      total.processingNs += entry.total_processing_time_ns;
      total.requests += entry.request_count;
      totals.set(key, total);
    }
  }
  return totals;
}

function sortedTotals(
  intervals: WorkloadMetricsInterval[],
  dimensions: GroupingDimension[],
) {
  return Array.from(aggregateTotals(intervals, dimensions).entries())
    .sort(([aKey, a], [bKey, b]) => b.processingNs - a.processingNs || aKey.localeCompare(bKey));
}

function timelinePoints(intervals: WorkloadMetricsInterval[]) {
  const sortedIntervals = [...intervals].sort((a, b) => a.start_ms - b.start_ms);
  const points: Array<{ timestamp: number; interval: WorkloadMetricsInterval | null }> = [];
  for (const interval of sortedIntervals) {
    const previous = points.at(-1)?.interval;
    if (previous && interval.start_ms > previous.end_ms) {
      points.push({ timestamp: previous.end_ms, interval: null });
    }
    points.push({ timestamp: interval.start_ms, interval });
  }
  if (sortedIntervals.length > 0) {
    const lastInterval = sortedIntervals.at(-1)!;
    points.push({ timestamp: lastInterval.end_ms, interval: lastInterval });
  }
  return points;
}

/** Selects buckets by total processing time, then builds every time metric from
 * the same ordered set so colors and ranking match between the charts. */
export function buildGroupedSeries(
  intervals: WorkloadMetricsInterval[],
  dimensions: GroupingDimension[],
  limit = TOP_BUCKET_LIMIT,
): WorkloadSeries[] {
  const selected = sortedTotals(intervals, dimensions).slice(0, limit);
  const points = timelinePoints(intervals);
  const selectedKeys = new Set(selected.map(([key]) => key));
  const valuesByPoint = points.map((point) => {
    const values = new Map<string, { processingNs: number; requests: number }>();
    for (const entry of point.interval?.entries ?? []) {
      const key = bucketKey(entry, dimensions);
      if (!selectedKeys.has(key)) continue;
      const current = values.get(key) ?? { processingNs: 0, requests: 0 };
      current.processingNs += entry.total_processing_time_ns;
      current.requests += entry.request_count;
      values.set(key, current);
    }
    return values;
  });

  return selected.map(([key, total]) => {
    const processingLoad: number[] = [];
    const requestRate: number[] = [];
    const averageDurationMs: number[] = [];
    for (let index = 0; index < points.length; index += 1) {
      const point = points[index];
      if (point.interval === null) {
        processingLoad.push(0);
        requestRate.push(0);
        averageDurationMs.push(0);
        continue;
      }
      const { processingNs, requests } = valuesByPoint[index].get(key)
        ?? { processingNs: 0, requests: 0 };
      const durationMs = Math.max(1, point.interval.end_ms - point.interval.start_ms);
      processingLoad.push(processingNs / (durationMs * NS_PER_MS));
      requestRate.push(requests / (durationMs / 1000));
      averageDurationMs.push(requests === 0 ? 0 : processingNs / requests / NS_PER_MS);
    }
    return {
      key,
      label: bucketLabel(total.entry, dimensions),
      legendParts: dimensions.map((dimension) => ({
        key: dimension,
        label: GROUPING_DIMENSIONS.find((candidate) => candidate.key === dimension)!.label,
        value: dimensionValue(total.entry, dimension) || 'Unknown',
      })),
      totalProcessingTimeNs: total.processingNs,
      totalRequests: total.requests,
      timestamps: points.map((point) => point.timestamp),
      processingLoad,
      requestRate,
      averageDurationMs,
    };
  });
}

export function buildGroupSummaries(
  intervals: WorkloadMetricsInterval[],
  dimensions: GroupingDimension[],
): WorkloadGroupSummary[] {
  const totals = sortedTotals(intervals, dimensions);
  const allProcessingNs = totals.reduce((sum, [, value]) => sum + value.processingNs, 0);
  const allRequests = totals.reduce((sum, [, value]) => sum + value.requests, 0);
  return totals.map(([key, value]) => ({
    key,
    label: bucketLabel(value.entry, dimensions),
    groupValues: Object.fromEntries(dimensions.map((dimension) => [
      dimension,
      dimensionValue(value.entry, dimension) || 'Unknown',
    ])),
    totalProcessingTimeNs: value.processingNs,
    totalRequests: value.requests,
    averageDurationMs: value.requests === 0 ? 0 : value.processingNs / value.requests / NS_PER_MS,
    processingSharePercent: allProcessingNs === 0 ? 0 : value.processingNs / allProcessingNs * 100,
    requestSharePercent: allRequests === 0 ? 0 : value.requests / allRequests * 100,
  }));
}

/** Calculates a request-weighted mean over the complete selected time window. */
export function buildWindowAverageRequestTimes(
  intervals: WorkloadMetricsInterval[],
  dimensions: GroupingDimension[],
): WorkloadRequestTimeSummary[] {
  const intervalAverages = new Map<string, number[]>();

  for (const interval of intervals) {
    const totals = new Map<string, { processingNs: number; requests: number }>();
    for (const entry of interval.entries) {
      const key = bucketKey(entry, dimensions);
      const total = totals.get(key) ?? { processingNs: 0, requests: 0 };
      total.processingNs += entry.total_processing_time_ns;
      total.requests += entry.request_count;
      totals.set(key, total);
    }
    for (const [key, total] of totals) {
      if (total.requests === 0) continue;
      const averages = intervalAverages.get(key) ?? [];
      averages.push(total.processingNs / total.requests / NS_PER_MS);
      intervalAverages.set(key, averages);
    }
  }

  const nearestRankPercentile = (values: number[], percentile: number) => {
    const sorted = [...values].sort((a, b) => a - b);
    return sorted[Math.ceil(percentile * sorted.length) - 1] ?? 0;
  };

  return buildGroupSummaries(intervals, dimensions)
    .filter((summary) => summary.totalRequests > 0)
    .map((summary) => {
      const averages = intervalAverages.get(summary.key) ?? [];
      return {
        ...summary,
        p95DurationMs: nearestRankPercentile(averages, 0.95),
        p99DurationMs: nearestRankPercentile(averages, 0.99),
        sampledIntervals: averages.length,
      };
    })
    .sort((a, b) => b.averageDurationMs - a.averageDurationMs || a.key.localeCompare(b.key));
}

export function calculateAverageIntervalDurationSeconds(
  timeWindowLengthMs: number,
  intervalCount: number,
): number | null {
  if (timeWindowLengthMs <= 0 || intervalCount <= 0) return null;
  return Math.round(timeWindowLengthMs / intervalCount / 1000);
}

/** Builds a complete 100% workload mix. Buckets outside the selected Top N are
 * retained as Other so the chart never understates the total. */
export function buildWorkloadShareSeries(
  intervals: WorkloadMetricsInterval[],
  dimensions: GroupingDimension[],
  limit = 8,
): WorkloadShareSeries[] {
  const selected = sortedTotals(intervals, dimensions).slice(0, limit);
  const selectedKeys = new Set(selected.map(([key]) => key));
  const points = timelinePoints(intervals);
  const values = new Map<string, number[]>();
  for (const [key] of selected) values.set(key, []);
  const other: number[] = [];
  let hasOther = false;

  for (const point of points) {
    const processingByKey = new Map<string, number>();
    let totalProcessingNs = 0;
    let otherProcessingNs = 0;
    for (const entry of point.interval?.entries ?? []) {
      const key = bucketKey(entry, dimensions);
      totalProcessingNs += entry.total_processing_time_ns;
      if (selectedKeys.has(key)) {
        processingByKey.set(key, (processingByKey.get(key) ?? 0) + entry.total_processing_time_ns);
      } else {
        otherProcessingNs += entry.total_processing_time_ns;
      }
    }
    for (const [key] of selected) {
      values.get(key)!.push(totalProcessingNs === 0
        ? 0
        : (processingByKey.get(key) ?? 0) / totalProcessingNs * 100);
    }
    other.push(totalProcessingNs === 0 ? 0 : otherProcessingNs / totalProcessingNs * 100);
    hasOther ||= otherProcessingNs > 0;
  }

  const timestamps = points.map((point) => point.timestamp);
  const result = selected.map(([key, value]) => ({
    key,
    label: bucketLabel(value.entry, dimensions),
    timestamps,
    percentages: values.get(key)!,
  }));
  if (hasOther) result.push({ key: '__other__', label: 'Other', timestamps, percentages: other });
  return result;
}

export function buildUserApiHeatmap(
  intervals: WorkloadMetricsInterval[],
  userLimit = 10,
  apiLimit = 10,
): WorkloadHeatmap {
  const userTotals = new Map<string, number>();
  const apiTotals = new Map<string, number>();
  for (const interval of intervals) {
    for (const entry of interval.entries) {
      const user = entry.username || 'Unknown';
      const apiLabel = entry.api_label || 'Unknown';
      userTotals.set(user, (userTotals.get(user) ?? 0) + entry.total_processing_time_ns);
      apiTotals.set(apiLabel, (apiTotals.get(apiLabel) ?? 0) + entry.total_processing_time_ns);
    }
  }
  const topKeys = (totals: Map<string, number>, limit: number) => Array.from(totals)
    .sort(([aKey, a], [bKey, b]) => b - a || aKey.localeCompare(bKey))
    .slice(0, limit)
    .map(([key]) => key);
  const users = topKeys(userTotals, userLimit);
  const apiLabels = topKeys(apiTotals, apiLimit);
  const userSet = new Set(users);
  const apiSet = new Set(apiLabels);
  const hasOtherUsers = Array.from(userTotals.keys()).some((key) => !userSet.has(key));
  const hasOtherApis = Array.from(apiTotals.keys()).some((key) => !apiSet.has(key));
  if (hasOtherUsers) users.push('Other');
  if (hasOtherApis) apiLabels.push('Other');
  const userIndexes = new Map(users.map((value, index) => [value, index]));
  const apiIndexes = new Map(apiLabels.map((value, index) => [value, index]));
  const processingSeconds = users.map(() => apiLabels.map(() => 0));
  for (const interval of intervals) {
    for (const entry of interval.entries) {
      const rawUser = entry.username || 'Unknown';
      const rawApi = entry.api_label || 'Unknown';
      const user = userSet.has(rawUser) ? rawUser : 'Other';
      const api = apiSet.has(rawApi) ? rawApi : 'Other';
      const row = userIndexes.get(user);
      const column = apiIndexes.get(api);
      if (row === undefined || column === undefined) continue;
      processingSeconds[row][column] += entry.total_processing_time_ns / 1_000_000_000;
    }
  }
  return { users, apiLabels, processingSeconds };
}

export function buildCollectionHealth(intervals: WorkloadMetricsInterval[]): WorkloadHealthPoint[] {
  return [...intervals]
    .sort((a, b) => a.start_ms - b.start_ms)
    .map((interval) => ({
      timestamp: interval.start_ms,
      activeUsers: new Set(interval.entries.map((entry) => entry.username)).size,
      aggregates: interval.entries.length,
      droppedRecords: interval.dropped_records,
    }));
}

export function countDroppedRecords(intervals: WorkloadMetricsInterval[]): number {
  return intervals.reduce((sum, interval) => sum + interval.dropped_records, 0);
}
